import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedPatient, upsertPatient, clearSelectedPatient } from '../../../../ReduxSlices/Patients/patientSlice';

const PatientForm = ({navigateTo}) => {
  const dispatch = useDispatch();
  const selectedPatient = useSelector(selectSelectedPatient);

  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    contact_info: '',
  });

  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedPatient) {
      setFormData({
        name: selectedPatient.name || '',
        age: selectedPatient.age || '',
        gender: selectedPatient.gender || '',
        contact_info: selectedPatient.contact_info || '',
      });
    }
  }, [selectedPatient]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' })); // Clear specific field error
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Patient name is required.';
    if (!formData.age) newErrors.age = 'Age is required.';
    if (!formData.gender) newErrors.gender = 'Please select a gender.';
    if (!formData.contact_info.trim()) newErrors.contact_info = 'Contact information is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
     if (!validate() || isSubmitting) return;

    setIsSubmitting(true);
    const isEditing = Boolean(selectedPatient);
    const method = isEditing ? "PUT" : "POST";
    const endpoint = isEditing
    ? `/api/v1/patients/${selectedPatient.id}`
    : `/api/v1/patients`;

    try {
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient: formData }),
      });

      if (!response.ok) {
         setStatus('Failed to save patient.');
         return;
      }

      const savedPatient = await response.json();
      
      //update global state list
      dispatch(upsertPatient(savedPatient));

      //clear selected
      dispatch(clearSelectedPatient());

      setStatus(
        isEditing
        ? "Patient updated successfully!"
        : "Patient added successfully!"
      );

      navigateTo("all_patients");

    } catch (error) {
      console.error('Error adding patient:', error);
      setStatus('An error occurred while adding the patient.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => setStatus(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  return (
    <div className="add">
      <h2>{selectedPatient ? "Edit Patient" : "Add Patient"}</h2>
      {status && (
        <p
          className={`notification mt-4 text-sm font-medium ${
            status.includes('success') ? 'text-green-600' : 'error-msg'
          }`}
        >
          {status}
        </p>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Name */}
        <div>
          <label htmlFor="patient-name">
            Name:{' '}
            <input
              id="patient-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </label>
          {errors.name && <p className="error-msg">{errors.name}</p>}
        </div>

        {/* Age */}
        <div>
          <label htmlFor="patient-age">
            Age:{' '}
            <input
              id="patient-age"
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </label>
          {errors.age && <p className="error-msg">{errors.age}</p>}
        </div>

        {/* Gender */}
       <div className="gender">
        <span>Gender:</span>
        <div className="gender-options">
        {['Male', 'Female', 'Non-binary', 'Prefer not to say'].map((g) => (
          <label key={g}  >
            <input
              type="radio"
              name="gender"
              value={g}
              checked={formData.gender === g}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            {g}
          </label>
        ))}
        </div>
       
      </div>

       <div>
        {errors.gender && <p className=" error-msg">{errors.gender}</p>}
        </div>


        {/* Contact Info */}
        <div>
          <label htmlFor="patient-contact">
            Contact Info:{' '}
            <input
              id="patient-contact"
              type="text"
              name="contact_info"
              value={formData.contact_info}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </label>
          {errors.contact_info && (
            <p className="error-msg">{errors.contact_info}</p>
          )}
        </div>

        <button className="primary wide" type="submit" disabled={isSubmitting} >
          { isSubmitting
          ? selectedPatient
            ? "Updating..."
            : "Adding..."
          : selectedPatient
          ? "Update Patient"
          : "Add Patient"
          }
        </button>
      </form>
    </div>
  );
};

export default PatientForm;
